import { memo, useEffect } from 'react';
import { ErrorCode, useDropzone } from 'react-dropzone';
import { useRouter } from 'next/router';

import { Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { photoUtils } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyFloorPlanPhotoWebApiHandler_upload,
  useRentalManagerUserPropertyFloorPlanPhotoWebApiHandler_uploadForFeed,
  useRentalManagerUserPropertyPhotoWebApiHandler_upload,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import {
  useIsOnReviewStep,
  useMultiplexBuildingSessionId,
  useMultiplexListingFlow,
  useMultiplexPhotoUpload,
} from '../../../../../../hooks/queries/multiplex';
import { useGetListingDetails } from '../../../../../../hooks/queries/properties';
import { useIsPhotoUploadRestricted } from '../../../../../../hooks/useIsPhotoUploadRestricted';
import { usePropertyEditMutation } from '../../../../../../hooks/usePropertyEditMutation';

import Photo from '../../../../../../model/Photo';

import { slowClient } from '../../../../../../http/zgApiClient';
import UploadDropzone from '../../../../../UploadDropzone';

import type { Dispatch, SetStateAction } from 'react';
import type { PhotoProps } from '@zg-rentals/maintenance-utils';
import type {
  UserPropertyFloorPlanPhotoUploadResponseWebDto,
  UserPropertyMultiplexPhotoUploadResponseWebDto,
  UserPropertyPhotoUploadResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { DatarouterError } from '@zg-rentals/web-base';

const uploadingCountMap: Record<string, number> = {};

const DEFAULT_UPLOAD_ERROR_MESSAGE = 'There was an error uploading your photo';

const getMultiplexUploadErrorMessage = (
  mux: UserPropertyMultiplexPhotoUploadResponseWebDto,
  listingAlias: string,
): string => {
  const unitForListing = mux.units.find((u) => u.listingAlias === listingAlias);

  return (
    unitForListing?.errorMessage ||
    mux.units.find((u) => u.errorMessage)?.errorMessage ||
    mux.errorMessage ||
    DEFAULT_UPLOAD_ERROR_MESSAGE
  );
};

type PhotoContext =
  | { type: 'property'; listingAlias: string }
  | { type: 'floorPlan'; floorPlanId: string; floorPlanIndex: number };

type Props = {
  photoContext: PhotoContext;
  isSmallDropzone?: boolean;
  smallDisplayText?: string;
  /** False lets the dropzone fill its grid cell instead of the fixed square used when a listing has no photos. */
  hasFixedEmptySize?: boolean;
  setUploadingCount: Dispatch<SetStateAction<number>>;
  uploadingCount: number;
  /** Parent appends uploaded photos immediately; required for multiplex thumbnails before refetch. */
  onUploadSuccess?: (uploadedPhotos: Array<PhotoProps>) => void;
};

const PhotoUpload = ({
  photoContext,
  isSmallDropzone = false,
  smallDisplayText,
  hasFixedEmptySize = true,
  setUploadingCount,
  uploadingCount,
  onUploadSuccess,
}: Props) => {
  const router = useRouter();
  const { enqueueToast } = useToast();

  const listingAlias =
    photoContext.type === 'property' ? photoContext.listingAlias : (router.query.listingAlias as string);

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const isOnReviewStep = useIsOnReviewStep();
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();
  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();
  /** Wizard multiplex steps upload to the building session; review uses listing photo upload. */
  const saveViaMultiplex =
    photoContext.type === 'property' && isOnReviewStep === false && Boolean(isInMultiplexListingFlow);

  const photos =
    photoContext.type === 'property'
      ? listingDetails?.propertyPhotos || []
      : listingDetails?.floorPlans?.[photoContext.floorPlanIndex]?.floorPlanPhotos || [];
  const isFeed = listingDetails?.isFeed ?? false;

  const isPhotoUploadRestricted = useIsPhotoUploadRestricted(listingAlias);

  const trackingKey = photoContext.type === 'property' ? photoContext.listingAlias : photoContext.floorPlanId;

  const { mutateAsync: uploadPropertyPhoto } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyPhotoWebApiHandler_upload,
    successToastMessage: 'Photo uploaded',
    httpClient: slowClient,
    meta: {
      invalidates: ['getMultiplexUnits', 'getGroupableProperties'],
      context: { api: 'RentalManagerUserPropertyPhotoWebApiHandler_upload' },
    },
  });

  const { mutateAsync: uploadFloorPlanPhoto } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanPhotoWebApiHandler_upload,
    successToastMessage: 'Photo uploaded',
    httpClient: slowClient,
    meta: { context: { api: 'RentalManagerUserPropertyFloorPlanPhotoWebApiHandler_upload' } },
  });

  const { mutateAsync: uploadFeedFloorPlanPhoto } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanPhotoWebApiHandler_uploadForFeed,
    successToastMessage: 'Photo uploaded',
    httpClient: slowClient,
    meta: { context: { api: 'RentalManagerUserPropertyFloorPlanPhotoWebApiHandler_uploadForFeed' } },
  });

  const { mutateAsync: multiplexUploadPhotoAsync } = useMultiplexPhotoUpload({
    httpClient: slowClient,
    meta: {
      invalidates: ['getMultiplexUnits', 'getGroupableProperties'],
    },
  });

  const trackedUploadingCount = uploadingCountMap[trackingKey];

  useEffect(() => {
    if (trackedUploadingCount && Number(trackedUploadingCount || 0) !== uploadingCount) {
      setUploadingCount(trackedUploadingCount);
    }

    if (!trackedUploadingCount && uploadingCount !== 0) {
      setUploadingCount(0);
    }
  }, [trackedUploadingCount, uploadingCount, setUploadingCount, trackingKey]);

  const onDropAccepted = async (acceptedFiles: Array<Blob>) => {
    const promiseArr: Array<
      Promise<
        | UserPropertyPhotoUploadResponseWebDto
        | UserPropertyFloorPlanPhotoUploadResponseWebDto
        | UserPropertyMultiplexPhotoUploadResponseWebDto
      >
    > = [];
    let errorArr: Array<string | undefined> = [];

    const validatedAcceptedFiles = await photoUtils.filterForValidFiles(acceptedFiles, onDropRejected);

    uploadingCountMap[trackingKey] = validatedAcceptedFiles.length;
    setUploadingCount(validatedAcceptedFiles.length);

    return photoUtils.preloadImages(validatedAcceptedFiles).then((preloadedFiles) => {
      // preloadImages can return undefined or drop failed loads; multiplex upload requires a Blob sourceFile.
      const filesToUpload = (Array.isArray(preloadedFiles) ? preloadedFiles : []).filter(
        (file): file is Blob => file instanceof Blob,
      );

      if (filesToUpload.length === 0) {
        delete uploadingCountMap[trackingKey];
        setUploadingCount(0);
        return;
      }

      if (filesToUpload.length !== validatedAcceptedFiles.length) {
        uploadingCountMap[trackingKey] = filesToUpload.length;
        setUploadingCount(filesToUpload.length);
      }

      filesToUpload.forEach((file) => {
        if (saveViaMultiplex) {
          promiseArr.push(
            multiplexUploadPhotoAsync({
              getParams: {
                multiplexBuildingSessionId: multiplexBuildingSessionId as string,
              },
              postData: { sourceFile: file },
            }),
          );
        } else if (photoContext.type === 'property') {
          promiseArr.push(
            uploadPropertyPhoto({
              getParams: { listingAlias: photoContext.listingAlias },
              postData: { sourceFile: file },
            }),
          );
        } else if (isFeed) {
          promiseArr.push(
            uploadFeedFloorPlanPhoto({
              getParams: { listingAlias, floorPlanId: photoContext.floorPlanId },
              postData: { sourceFile: file },
            }),
          );
        } else {
          promiseArr.push(
            uploadFloorPlanPhoto({
              getParams: { floorPlanId: photoContext.floorPlanId },
              postData: { sourceFile: file },
            }),
          );
        }
      });

      if (promiseArr.length > 0) {
        return Promise.allSettled(promiseArr)
          .then((response) => {
            // filter out error objects from response array
            // appear when user uploads photos in offline / unstable network mode (user sees blank photo placeholder)
            const photoArray = (
              response as unknown as Array<{
                error: unknown;
                url?: string;
                status: 'fulfilled' | 'rejected';
                reason?: DatarouterError;
                value: PhotoProps | UserPropertyMultiplexPhotoUploadResponseWebDto;
              }>
            )
              .filter((responseObject) => {
                if (responseObject.status === 'rejected') {
                  errorArr.push(responseObject?.reason?.message || DEFAULT_UPLOAD_ERROR_MESSAGE);
                  return false;
                }

                // Handle multiplex response
                if (saveViaMultiplex) {
                  const value = responseObject.value as unknown;
                  if (
                    value &&
                    typeof value === 'object' &&
                    'units' in value &&
                    Array.isArray((value as UserPropertyMultiplexPhotoUploadResponseWebDto).units)
                  ) {
                    const mux = value as UserPropertyMultiplexPhotoUploadResponseWebDto;
                    const unitForListing = mux.units.find((u) => u.listingAlias === listingAlias);

                    if (!mux.success) {
                      errorArr.push(getMultiplexUploadErrorMessage(mux, listingAlias));
                      return false;
                    }

                    if (!unitForListing?.success || !unitForListing.photo) {
                      errorArr.push(unitForListing?.errorMessage || mux.errorMessage || DEFAULT_UPLOAD_ERROR_MESSAGE);
                      return false;
                    }

                    return true;
                  }

                  return false;
                }

                const foundError = Boolean(responseObject.error && responseObject.url === undefined);

                if (foundError) {
                  errorArr.push(responseObject?.reason?.message || DEFAULT_UPLOAD_ERROR_MESSAGE);
                }

                return !foundError;
              })
              .map((successfulPhoto) => {
                // Handle multiplex response
                if (saveViaMultiplex) {
                  const value = successfulPhoto.value as unknown;
                  if (
                    value &&
                    typeof value === 'object' &&
                    'units' in value &&
                    Array.isArray((value as UserPropertyMultiplexPhotoUploadResponseWebDto).units)
                  ) {
                    const mux = value as UserPropertyMultiplexPhotoUploadResponseWebDto;
                    const unitForListing = mux.units.find((u) => u.listingAlias === listingAlias);
                    return Photo(unitForListing?.photo ?? {});
                  }
                }

                return Photo(successfulPhoto.value as PhotoProps);
              });

            /** Clear placeholders before appending real photos so the gallery does not briefly show both. */
            delete uploadingCountMap[trackingKey];
            setUploadingCount(0);

            /** Update parent gallery before cache invalidation. */
            if (photoArray.length > 0) {
              onUploadSuccess?.(photoArray);
            }

            if (errorArr.filter((err) => err !== undefined).length > 0) {
              throw new Error();
            }

            return photoArray;
          })
          .catch(() => {
            new Set(errorArr).forEach((err) => {
              logError({ error: err, context: 'uploadPhoto' });
              enqueueToast(<Toast tone="critical">{err} </Toast>);
            });
          })
          .finally(() => {
            delete uploadingCountMap[trackingKey];
            setUploadingCount(0);
            errorArr = [];
          });
      }

      return;
    });
  };

  const onDropRejected = (errorData?: Array<{ errors?: Array<{ message: string; code?: string }> }>) => {
    const error = errorData?.[0]?.errors?.[0];
    enqueueToast(
      <Toast tone="critical">
        {error?.code === ErrorCode.FileInvalidType ? 'File must be an image' : error?.message}
      </Toast>,
    );
  };

  const isUploadDisabled = uploadingCount > 0 || isPhotoUploadRestricted;

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, acceptedFiles } = useDropzone({
    accept: 'image/*',
    onDropAccepted,
    onDropRejected,
    disabled: isUploadDisabled,
  });

  const getRootPropsWithTabIndex = () => ({
    ...getRootProps({ isFocused, isDragAccept, isDragReject, acceptedFiles }),
    tabIndex: isSmallDropzone ? 0 : -1,
  });

  return (
    <UploadDropzone
      isSmallDropzone={isSmallDropzone}
      smallDisplayText={smallDisplayText}
      isDisabled={isUploadDisabled}
      isEmpty={hasFixedEmptySize && photos.length === 0 && uploadingCount === 0}
      rootProps={getRootPropsWithTabIndex()}
      inputProps={getInputProps()}
    />
  );
};

export default memo(PhotoUpload);
